import { Plus, Trash2, Sparkles, Home } from 'lucide-react';
import Toggle from '../components/Toggle';
import { ic, lc, emptyLoss } from '../helpers';
import {
  PROPERTY_TYPES,
  ROOF_MATERIALS,
  ROOF_SHAPES,
  CONSTRUCTION_TYPES,
  BASEMENT_TYPES,
} from '../constants';

// ─────────────────────────────────────────
// PROPERTY CARD
// One card per property. Lots of nested toggles + grids.
// Used only inside Step3Properties so it lives here.
// ─────────────────────────────────────────
function PropertyCard({ property, index, total, onChange, onRemove, aiExtracted }) {
  const update = (key, val) => onChange(index, { ...property, [key]: val });

  const updateLoss = (li, key, val) => {
    const updated = [...(property.losses || [])];
    updated[li] = { ...updated[li], [key]: val };
    update('losses', updated);
  };

  const addLoss = () => update('losses', [...(property.losses || []), emptyLoss()]);

  const removeLoss = (li) => {
    const updated = (property.losses || []).filter((_, i) => i !== li);
    update('losses', updated);
    if (updated.length === 0) update('hasPriorLosses', false);
  };

  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Home size={15} className="text-blue-600" />
          <span className="text-sm font-semibold text-slate-700">Property {index + 1}</span>
          {aiExtracted && (
            <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-lg">
              <Sparkles size={10} /> AI filled
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={property.propertyType}
            onChange={e => update('propertyType', e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-500 bg-white"
          >
            {PROPERTY_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          {total > 1 && (
            <button onClick={() => onRemove(index)} className="p-1 text-slate-400 hover:text-red-500 transition">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="p-4 space-y-4">

        <div>
          <label className={lc}>Property Address</label>
          <input value={property.address} onChange={e => update('address', e.target.value)} placeholder="123 Main St, City, State" className={ic} />
        </div>
        <div>
          <label className={lc}>To whom is the home titled?</label>
          <input value={property.titleHolder} onChange={e => update('titleHolder', e.target.value)} placeholder="Full name(s)" className={ic} />
        </div>

        {/* Mortgage */}
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle label="Do you have a mortgage?" checked={property.hasMortgage} onChange={v => update('hasMortgage', v)} />
          {property.hasMortgage && (
            <Toggle label="Is the premium paid via escrow?" checked={property.escrowPremium} onChange={v => update('escrowPremium', v)} />
          )}
        </div>

        {property.hasMortgage && (
          <div className="space-y-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Mortgage Lender Info</p>
            <div>
              <label className={lc}>Lender / Bank Name</label>
              <input value={property.mortgageeName || ''} onChange={e => update('mortgageeName', e.target.value)} placeholder="e.g. Wells Fargo Bank" className={ic} />
            </div>
            <div>
              <label className={lc}>Lender Mailing Address</label>
              <input value={property.mortgageeAddress || ''} onChange={e => update('mortgageeAddress', e.target.value)} placeholder="PO Box or street address" className={ic} />
            </div>
          </div>
        )}

        {/* Home construction details */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Home Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lc}>Year Built</label>
              <input type="number" value={property.yearBuilt || ''} onChange={e => update('yearBuilt', e.target.value)} placeholder="e.g. 1995" className={ic} />
            </div>
            <div>
              <label className={lc}>Finished Sq. Footage</label>
              <input type="number" value={property.squareFootage || ''} onChange={e => update('squareFootage', e.target.value)} placeholder="e.g. 2400" className={ic} />
            </div>
            <div>
              <label className={lc}>Number of Floors</label>
              <input type="number" value={property.numFloors || ''} onChange={e => update('numFloors', e.target.value)} placeholder="e.g. 2" className={ic} />
            </div>
            <div>
              <label className={lc}>Construction Type</label>
              <select value={property.constructionType || ''} onChange={e => update('constructionType', e.target.value)} className={ic}>
                <option value="">Select...</option>
                {CONSTRUCTION_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={lc}>Swimming Pool</label>
              <select
                value={property.hasPool === true ? 'yes' : property.hasPool === false ? 'no' : ''}
                onChange={e => update('hasPool', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                className={ic}
              >
                <option value="">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        </div>

        {/* Basement */}
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle label="Does this home have a basement?" checked={property.hasBasement} onChange={v => update('hasBasement', v)} />
        </div>
        {property.hasBasement && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lc}>Basement Finish Type</label>
              <select value={property.basementType || ''} onChange={e => update('basementType', e.target.value)} className={ic}>
                <option value="">Select...</option>
                {BASEMENT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={lc}>Basement Sq. Footage</label>
              <input type="number" value={property.basementSqFt || ''} onChange={e => update('basementSqFt', e.target.value)} placeholder="e.g. 800" className={ic} />
            </div>
          </div>
        )}

        {/* Roof */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Roof</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={lc}>Year Last Replaced</label>
              <input type="number" value={property.roofYear} onChange={e => update('roofYear', e.target.value)} placeholder="e.g. 2018" className={ic} />
            </div>
            <div>
              <label className={lc}>Material</label>
              <select value={property.roofMaterial} onChange={e => update('roofMaterial', e.target.value)} className={ic}>
                <option value="">Select...</option>
                {ROOF_MATERIALS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={lc}>Shape</label>
              <select value={property.roofShape || ''} onChange={e => update('roofShape', e.target.value)} className={ic}>
                <option value="">Select...</option>
                {ROOF_SHAPES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            <Toggle label="Class IV hail-resistant roof?" checked={property.roofClassIV} onChange={v => update('roofClassIV', v)} />
          </div>
        </div>

        {/* Residence Usage */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Residence Usage</p>
          <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            {[
              { key: 'isVacant',             label: 'Is the residence vacant?' },
              { key: 'isUnderConstruction',  label: 'Is the residence under construction or renovation?' },
              { key: 'isForSale',            label: 'Is the residence currently for sale?' },
              { key: 'isHistoricRegistry',   label: 'Is this location listed on national, state, or local register of historic places?' },
            ].map(({ key, label }) => (
              <Toggle key={key} label={label} checked={!!property[key]} onChange={v => update(key, v)} />
            ))}
          </div>
        </div>

        {/* Installed Systems */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Installed Systems</p>
          <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            {[
              { key: 'centralFireAlarm', label: 'Centrally monitored fire alarm' },
              { key: 'centralBurglarAlarm', label: 'Centrally monitored burglar alarm' },
              { key: 'lowTempSensor', label: 'Low-temperature sensor' },
              { key: 'sprinklerSystem', label: 'In-home sprinkler system' },
              { key: 'permanentGenerator', label: 'Permanently installed generator' },
              { key: 'gatedCommunity', label: 'Located in a gated community' },
              { key: 'autoWaterShutoff', label: 'Smart/auto water shut-off device' },
            ].map(({ key, label }) => (
              <Toggle key={key} label={label} checked={property[key]} onChange={v => update(key, v)} />
            ))}
          </div>
        </div>

        {/* Prior Losses */}
        <div>
          <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            <Toggle
              label="Any prior losses at this location in the past 5 years?"
              checked={property.hasPriorLosses}
              onChange={v => {
                update('hasPriorLosses', v);
                if (!v) update('losses', []);
              }}
            />
          </div>
          {property.hasPriorLosses && (
            <div className="space-y-3 mt-3">
              {(property.losses || []).map((loss, li) => (
                <div key={li} className="border border-slate-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Loss {li + 1}</span>
                    <button onClick={() => removeLoss(li)} className="text-xs text-slate-400 hover:text-red-500 transition">
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={lc}>Date of Loss</label>
                      <input
                        type="date"
                        value={loss.lossDate || ''}
                        onChange={e => updateLoss(li, 'lossDate', e.target.value)}
                        className={ic}
                      />
                    </div>
                    <div>
                      <label className={lc}>Loss Type</label>
                      <input
                        value={loss.lossType || ''}
                        onChange={e => updateLoss(li, 'lossType', e.target.value)}
                        placeholder="e.g. Water, Fire, Wind"
                        className={ic}
                      />
                    </div>
                    <div>
                      <label className={lc}>Amount Paid ($)</label>
                      <input
                        type="number"
                        value={loss.amountPaid || ''}
                        onChange={e => updateLoss(li, 'amountPaid', e.target.value)}
                        placeholder="e.g. 15000"
                        className={ic}
                      />
                    </div>
                    <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200 self-end">
                      <Toggle
                        label="Claim closed?"
                        checked={!!loss.claimClosed}
                        onChange={v => updateLoss(li, 'claimClosed', v)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={addLoss}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition py-1"
              >
                <Plus size={13} /> Add loss
              </button>
            </div>
          )}
        </div>

        {/* Year Systems Last Updated */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Year Systems Last Updated</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'waterHeaterYear', label: 'Water Heater' },
              { key: 'hvacYear', label: 'HVAC' },
              { key: 'plumbingYear', label: 'Plumbing' },
              { key: 'electricalYear', label: 'Electrical' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className={lc}>{label}</label>
                <input type="number" value={property[key]} onChange={e => update(key, e.target.value)} placeholder="e.g. 2015" className={ic} />
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// STEP 3 — PROPERTIES LIST
// Iterates over properties[] and renders one PropertyCard each.
// ─────────────────────────────────────────
export default function Step3Properties({
  properties,
  aiExtractedProps,
  onUpdate,
  onAdd,
  onRemove,
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Your Properties</h2>
          {aiExtractedProps.length > 0 && (
            <p className="text-xs text-purple-600 mt-0.5 flex items-center gap-1">
              <Sparkles size={10} /> Pre-filled by AI — please review
            </p>
          )}
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
        >
          <Plus size={13} /> Add Property
        </button>
      </div>
      {properties.map((property, index) => (
        <PropertyCard
          key={index}
          property={property}
          index={index}
          total={properties.length}
          onChange={onUpdate}
          onRemove={onRemove}
          aiExtracted={aiExtractedProps.includes(index)}
        />
      ))}
    </div>
  );
}